import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useRef, useState } from 'react'
import { IoChevronDownOutline, IoLogOutOutline, IoPerson } from 'react-icons/io5'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { accountSelector, logout } from '@/store/accountSlice'
import { capitalizeSentence as c } from '@/utils'

function AccountAvatar() {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const { user } = useSelector(accountSelector)
  const dispatch = useDispatch()
  const theme = useTheme()

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleLogout = async () => {
    handleClose()
    dispatch(logout())
  }

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          p: 1,
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
          },
        }}>
        <Avatar
          src={user?.photo || ''}
          sx={{
            height: 36,
            width: 36,
            border: '2px solid',
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        />
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <IoChevronDownOutline
            size={16}
            style={{
              color: 'white',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>
      </Box>

      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[8],
              mt: 1,
            },
          },
        }}>
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.1,
            )}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          }}>
          {user &&
            (() => {
              const [firstName] = c(user.name).split(' ')
              const [lastName] = c(user.name).split(' ').slice(-1)

              return (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={user.photo || ''}
                    sx={{
                      height: 48,
                      width: 48,
                      border: '2px solid',
                      borderColor: 'background.paper',
                    }}
                  />
                  <Box flex={1} minWidth={0}>
                    <Typography
                      color="text.primary"
                      variant="subtitle1"
                      fontWeight={700}
                      noWrap>
                      {firstName} {lastName}
                    </Typography>
                    <Chip
                      label={user.type}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </Stack>
              )
            })()}
        </Box>

        <Divider />

        <Box sx={{ py: 1 }}>
          <MenuItem
            component={Link}
            to="/perfil"
            onClick={handleClose}
            sx={{
              mx: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <IoPerson size={18} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle2" fontWeight={600}>
                  Meu Perfil
                </Typography>
              }
              secondary="Gerenciar perfil"
            />
          </MenuItem>
        </Box>

        <Divider sx={{ mx: 2 }} />

        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<IoLogOutOutline />}
            onClick={handleLogout}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              py: 1,
            }}>
            Sair da Conta
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default AccountAvatar
